import { useMemo } from "react"
import { z } from "zod"
import { useAuth0 } from "@auth0/auth0-react"
import { useRouterState } from "@tanstack/react-router"
import { arrayMove } from "@dnd-kit/sortable"

import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { parseViewParam, targetNamesView } from "~/hooks/useViewRoute"

/**
 * Sidebar favorites (pages, saved views and presets), client-only, per org + user. `route` is the
 * page URL minus `TRANSIENT_PARAMS` (a view favorite keeps `?view=`) and doubles as the identity;
 * the icon is resolved from it (`useNavModule`). `context` is snapshotted so generic names like "Default" stay distinguishable.
 */
const FavoriteEntrySchema = z.object({
  route: z.string(),
  label: z.string(),
  context: z.string().optional(),
  subtitle: z.string().optional(),
  folderId: z.string().optional(),
})

const FavoriteFolderSchema = z.object({
  type: z.literal("folder"),
  id: z.string(),
  name: z.string(),
  expanded: z.boolean().optional(),
})

/** Display order; a favorite sits in the folder its `folderId` names, if that folder exists. */
const FavoriteListSchema = z.array(z.union([FavoriteFolderSchema, FavoriteEntrySchema]))

export type FavoriteEntry = z.infer<typeof FavoriteEntrySchema>
export type FavoriteFolder = z.infer<typeof FavoriteFolderSchema>
export type FavoriteListItem = FavoriteEntry | FavoriteFolder

const EMPTY: FavoriteListItem[] = []

export const isFolder = (item: FavoriteListItem): item is FavoriteFolder => "type" in item

export const folderDndId = (id: string) => `folder:${id}`
export const isFolderDndId = (dndId: string) => dndId.startsWith("folder:")
export const ROOT_DROP_ID = "favorites-root"
export const favoriteDndId = (item: FavoriteListItem) =>
  isFolder(item) ? folderDndId(item.id) : item.route

const inFolder = (entry: FavoriteEntry, folderId?: string): FavoriteEntry => {
  const { folderId: _, ...rest } = entry
  return folderId ? { ...rest, folderId } : rest
}

/** The folder a favorite shows in; `undefined` at the top level, including when its folder is gone. */
export function parentFolderId(items: FavoriteListItem[], entry: FavoriteEntry) {
  const { folderId } = entry
  return folderId && items.some((i) => isFolder(i) && i.id === folderId) ? folderId : undefined
}

/** Top-level items in list order, plus each folder's favorites in list order. */
export function groupByFolder(items: FavoriteListItem[]) {
  const entriesByFolder = new Map<string, FavoriteEntry[]>()
  for (const item of items) {
    const folderId = !isFolder(item) && parentFolderId(items, item)
    if (folderId) entriesByFolder.set(folderId, [...(entriesByFolder.get(folderId) ?? []), item])
  }
  return {
    topLevel: items.filter((i) => isFolder(i) || !parentFolderId(items, i)),
    entriesByFolder,
  }
}

/** Favorites as the sidebar lists them, each folder's favorites in its place (expanded or not). */
export function favoritesInSidebarOrder(items: FavoriteListItem[]): FavoriteEntry[] {
  const { topLevel, entriesByFolder } = groupByFolder(items)
  return topLevel.flatMap((item) =>
    isFolder(item) ? (entriesByFolder.get(item.id) ?? []) : [item],
  )
}

/**
 * The list after dropping `activeId` on `overId`. A favorite dropped on a folder joins it at the end,
 * on a favorite takes its place and its folder. Anything dropped on the section header goes to the
 * top of the list, out of its folder. A folder only moves among the top level, taking the place of
 * whatever it lands on (or that item's folder).
 */
export function applyFavoriteDrop(items: FavoriteListItem[], activeId: string, overId: string) {
  const from = items.findIndex((i) => favoriteDndId(i) === activeId)
  const active = items[from]
  if (!active || activeId === overId) return items
  const rest = items.filter((_, i) => i !== from)

  if (isFolder(active)) {
    if (overId === ROOT_DROP_ID) return [active, ...rest]
    const over = items.find((i) => favoriteDndId(i) === overId)
    const parent = over && !isFolder(over) && parentFolderId(items, over)
    const targetId = parent ? folderDndId(parent) : overId
    const to = items.findIndex((i) => favoriteDndId(i) === targetId)
    return to === -1 || to === from ? items : arrayMove(items, from, to)
  }

  if (overId === ROOT_DROP_ID) return [inFolder(active), ...rest]
  const over = items.find((i) => favoriteDndId(i) === overId)
  if (!over) return items

  if (isFolder(over)) {
    if (parentFolderId(items, active) === over.id) return items
    const lastChild = rest.findLastIndex(
      (i) => i === over || (!isFolder(i) && parentFolderId(rest, i) === over.id),
    )
    return rest.toSpliced(lastChild + 1, 0, inFolder(active, over.id))
  }

  const to = items.indexOf(over)
  return arrayMove(items, from, to).with(to, inFolder(active, parentFolderId(items, over)))
}

const viewTargetOf = (route: string) =>
  parseViewParam(new URLSearchParams(route.split("?")[1]).get("view") ?? undefined)

const namesView = (route: string, viewId: number) => targetNamesView(viewTargetOf(route), viewId)

/** What a favorite points at, for tracking. */
export function favoriteTypeOf(route: string) {
  const target = viewTargetOf(route)
  if (!target) return "Page"
  return "presetId" in target ? "Preset" : "Saved View"
}

// Serialized values that only restate a param's default (e.g. `isGroup=false`).
const DEFAULT_VALUES = new Set(["", "false", "null", "undefined", "%5B%5D"])

/**
 * A route in comparable form: params holding only a default are dropped (plus `dropKeys`), so the
 * same page matches however its URL was built. Order and encoding are kept as the router wrote them.
 */
export function canonicalRoute(route: string, dropKeys: readonly string[] = []) {
  const [path, query = ""] = route.split("#")[0]!.split("?")
  const kept = query
    .split("&")
    .filter(Boolean)
    .filter((pair) => {
      const [key = "", value = ""] = pair.split("=")
      return !dropKeys.includes(decodeURIComponent(key)) && !DEFAULT_VALUES.has(value)
    })
  return kept.length ? `${path}?${kept.join("&")}` : path!
}

/**
 * Whether `favoriteRoute` is the page `route` names or one of its tabs (`/product/X` →
 * `/product/X/overview`), with the same params.
 */
export function isRouteOrTab(favoriteRoute: string, route: string) {
  const [favoritePath = "", favoriteQuery = ""] = canonicalRoute(favoriteRoute).split("?")
  const [path = "", query = ""] = canonicalRoute(route).split("?")
  const params = (q: string) => q.split("&").filter(Boolean).sort().join("&")
  const onPage = favoritePath === path || favoritePath.startsWith(`${path}/`)
  return onPage && params(favoriteQuery) === params(query)
}

// Params about how a page was reached or what's selected on it, not which page it is.
export const TRANSIENT_PARAMS = ["view", "run", "fromFiscalYear", "poIds", "purchasePlanId"]

/**
 * The URL on screen in comparable form; `{ page: true }` drops `TRANSIENT_PARAMS`, giving a page
 * favorite's route. Uses the resolved location, so while the next page loads this still names the
 * page shown.
 */
export function useCurrentRoute({ page = false } = {}) {
  return useRouterState({
    select: (s) =>
      canonicalRoute((s.resolvedLocation ?? s.location).href, page ? TRANSIENT_PARAMS : []),
  })
}

/** The list without the folder; its favorites take its place at the top level. */
export function removeFolder(items: FavoriteListItem[], id: string) {
  const children = items.filter(
    (i): i is FavoriteEntry => !isFolder(i) && parentFolderId(items, i) === id,
  )
  const moved = new Set<FavoriteListItem>(children)
  return items.flatMap((i) => {
    if (isFolder(i) && i.id === id) return children.map((c) => inFolder(c))
    return moved.has(i) ? [] : [i]
  })
}

export function useFavorites() {
  const { user } = useAuth0()
  const [items, setItems] = useSafeLocalStorage<FavoriteListItem[]>(
    `favorites.${user?.sub ?? ""}`,
    EMPTY,
    FavoriteListSchema,
  )

  return useMemo(() => {
    const favorites = items.filter((i): i is FavoriteEntry => !isFolder(i))
    const routes = new Set(favorites.map((f) => canonicalRoute(f.route)))
    const isSame = (a: string, b: string) => canonicalRoute(a) === canonicalRoute(b)
    const remove = (route: string) =>
      setItems(items.filter((i) => isFolder(i) || !isSame(i.route, route)))
    const updateFolder = (id: string, patch: Partial<FavoriteFolder>) =>
      setItems(items.map((i) => (isFolder(i) && i.id === id ? { ...i, ...patch } : i)))

    return {
      favorites,
      items,
      isFavorite: (route: string) => routes.has(canonicalRoute(route)),
      isInFolder: (route: string) => {
        const entry = favorites.find((f) => isSame(f.route, route))
        return !!entry && !!parentFolderId(items, entry)
      },
      /** Adds the entry to the end, or removes it if already favorited. Returns whether it's now a favorite. */
      toggle: (entry: FavoriteEntry) => {
        const exists = routes.has(canonicalRoute(entry.route))
        if (exists) remove(entry.route)
        else setItems([...items, { ...entry, route: canonicalRoute(entry.route) }])
        return !exists
      },
      remove,
      /**
       * Removes every favorite of a saved view (the same view can be favorited on several
       * entities, e.g. collections). View ids are unique per org. Returns whether any were removed.
       */
      removeView: (viewId: number) => {
        const kept = items.filter((i) => isFolder(i) || !namesView(i.route, viewId))
        if (kept.length === items.length) return false
        setItems(kept)
        return true
      },
      /** Refreshes the label of every favorite of a (renamed) saved view. No-op when unchanged. */
      relabelView: (viewId: number, label: string) => {
        const names = (i: FavoriteListItem) => !isFolder(i) && namesView(i.route, viewId)
        if (!items.some((i) => names(i) && !isFolder(i) && i.label !== label)) return
        setItems(items.map((i) => (names(i) ? { ...i, label } : i)))
      },
      /**
       * Applies a drag-and-drop. Returns `null` when the list didn't change, else the dragged
       * favorite's folder before and after (both `undefined` for a dragged folder).
       */
      drop: (activeId: string, overId: string) => {
        const next = applyFavoriteDrop(items, activeId, overId)
        if (next === items) return null
        setItems(next)
        const folderIn = (list: FavoriteListItem[]) => {
          const entry = list.find((i): i is FavoriteEntry => !isFolder(i) && i.route === activeId)
          return entry && parentFolderId(list, entry)
        }
        return { fromFolder: folderIn(items), toFolder: folderIn(next) }
      },
      /** Adds a folder at the end, filing the favorite at `route` into it when given. */
      createFolder: (name: string, route?: string) => {
        const id = crypto.randomUUID()
        const next: FavoriteListItem[] = [...items, { type: "folder", id, name, expanded: true }]
        setItems(route ? applyFavoriteDrop(next, route, folderDndId(id)) : next)
      },
      /**
       * Files the favorite into the folder, or out of any folder to the top of the list. Returns
       * whether it moved.
       */
      moveToFolder: (route: string, folderId: string | undefined) => {
        const entry = favorites.find((f) => f.route === route)
        if (!entry || parentFolderId(items, entry) === folderId) return false
        setItems(applyFavoriteDrop(items, route, folderId ? folderDndId(folderId) : ROOT_DROP_ID))
        return true
      },
      renameFolder: (id: string, name: string) => updateFolder(id, { name }),
      setFolderExpanded: (id: string, expanded: boolean) => updateFolder(id, { expanded }),
      deleteFolder: (id: string) => setItems(removeFolder(items, id)),
    }
  }, [items, setItems])
}
